import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, CalendarDays, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { HOSPITAL, SERVICES } from "../lib/hospitalData";
import { createAppointment, createEnquiry } from "../lib/api";

export default function Contact() {
  const [tab, setTab] = useState("appointment");
  const [loading, setLoading] = useState(false);

  const [appt, setAppt] = useState({ name: "", phone: "", email: "", department: SERVICES[0].name, preferred_date: "", preferred_time: "", message: "" });
  const [enq, setEnq] = useState({ name: "", phone: "", email: "", subject: "", message: "" });

  const submitAppt = async (e) => {
    e.preventDefault();
    if (!appt.name || !appt.phone || !appt.department || !appt.preferred_date) {
      toast.error("Please fill in name, phone, department and preferred date.");
      return;
    }
    try {
      setLoading(true);
      await createAppointment(appt);
      toast.success("Appointment request received. Our team will contact you shortly.");
      setAppt({ name: "", phone: "", email: "", department: SERVICES[0].name, preferred_date: "", preferred_time: "", message: "" });
    } catch {
      toast.error("Could not submit. Please try again or call us directly.");
    } finally { setLoading(false); }
  };

  const submitEnq = async (e) => {
    e.preventDefault();
    if (!enq.name || !enq.phone || !enq.message) {
      toast.error("Please fill name, phone and message.");
      return;
    }
    try {
      setLoading(true);
      await createEnquiry(enq);
      toast.success("Thank you! Your enquiry has been received.");
      setEnq({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-white border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/15 transition";

  return (
    <main data-testid="page-contact">
      <Toaster position="top-right" richColors />
      <section className="bg-brand-cream relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="container-x py-14 md:py-24 relative">
          <div className="text-xs tracking-[0.25em] uppercase text-brand-crimson font-semibold">Contact & Appointments</div>
          <h1 className="font-display text-[34px] sm:text-5xl lg:text-6xl font-bold text-brand-navy-deep mt-4 max-w-4xl leading-[1.05]">
            Let's take the <span className="italic text-brand-crimson">first step</span> together.
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-slate-700 max-w-3xl leading-relaxed">
            Book an appointment, ask a question or visit us in person — we'll guide you to the right specialist and care plan.
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container-x grid lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-border rounded-2xl shadow-[0_24px_60px_-30px_rgba(19,60,121,0.25)]">
              <div className="flex border-b border-border">
                <button data-testid="tab-appointment" onClick={() => setTab("appointment")} className={`flex-1 px-3 sm:px-6 py-3.5 text-xs sm:text-sm font-semibold transition ${tab==="appointment" ? "text-brand-crimson border-b-2 border-brand-crimson" : "text-slate-600 hover:text-brand-navy-deep"}`}>
                  <CalendarDays className="w-4 h-4 inline mr-1.5 sm:mr-2" /> Book Appointment
                </button>
                <button data-testid="tab-enquiry" onClick={() => setTab("enquiry")} className={`flex-1 px-3 sm:px-6 py-3.5 text-xs sm:text-sm font-semibold transition ${tab==="enquiry" ? "text-brand-crimson border-b-2 border-brand-crimson" : "text-slate-600 hover:text-brand-navy-deep"}`}>
                  <Send className="w-4 h-4 inline mr-1.5 sm:mr-2" /> General Enquiry
                </button>
              </div>

              {tab === "appointment" ? (
                <form onSubmit={submitAppt} className="p-7 space-y-4" data-testid="form-appointment">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Full Name *</label>
                      <input data-testid="appt-name" className={inputCls} value={appt.name} onChange={(e)=>setAppt({...appt,name:e.target.value})} placeholder="Your full name" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Phone *</label>
                      <input data-testid="appt-phone" className={inputCls} value={appt.phone} onChange={(e)=>setAppt({...appt,phone:e.target.value})} placeholder="+91 9XXXXXXXXX" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Email</label>
                    <input type="email" data-testid="appt-email" className={inputCls} value={appt.email} onChange={(e)=>setAppt({...appt,email:e.target.value})} placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Department / Specialty *</label>
                    <select data-testid="appt-department" className={inputCls} value={appt.department} onChange={(e)=>setAppt({...appt,department:e.target.value})}>
                      {SERVICES.map((s)=>(<option key={s.id} value={s.name}>{s.name}</option>))}
                    </select>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Preferred Date *</label>
                      <input type="date" data-testid="appt-date" className={inputCls} value={appt.preferred_date} onChange={(e)=>setAppt({...appt,preferred_date:e.target.value})} min={new Date().toISOString().slice(0,10)} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Preferred Time</label>
                      <input type="time" data-testid="appt-time" className={inputCls} value={appt.preferred_time} onChange={(e)=>setAppt({...appt,preferred_time:e.target.value})} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Reason for visit</label>
                    <textarea data-testid="appt-message" className={`${inputCls} min-h-[100px]`} value={appt.message} onChange={(e)=>setAppt({...appt,message:e.target.value})} placeholder="Briefly describe your symptoms or reason..." />
                  </div>
                  <button data-testid="submit-appointment" type="submit" disabled={loading} className="btn-primary w-full sm:w-auto disabled:opacity-60">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <CalendarDays className="w-4 h-4" />} Submit Request
                  </button>
                </form>
              ) : (
                <form onSubmit={submitEnq} className="p-5 md:p-7 space-y-4" data-testid="form-enquiry">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Full Name *</label>
                      <input data-testid="enq-name" className={inputCls} value={enq.name} onChange={(e)=>setEnq({...enq,name:e.target.value})} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Phone *</label>
                      <input data-testid="enq-phone" className={inputCls} value={enq.phone} onChange={(e)=>setEnq({...enq,phone:e.target.value})} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Email</label>
                    <input type="email" data-testid="enq-email" className={inputCls} value={enq.email} onChange={(e)=>setEnq({...enq,email:e.target.value})} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Subject</label>
                    <input data-testid="enq-subject" className={inputCls} value={enq.subject} onChange={(e)=>setEnq({...enq,subject:e.target.value})} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Message *</label>
                    <textarea data-testid="enq-message" className={`${inputCls} min-h-[140px]`} value={enq.message} onChange={(e)=>setEnq({...enq,message:e.target.value})} placeholder="How can we help?" />
                  </div>
                  <button data-testid="submit-enquiry" type="submit" disabled={loading} className="btn-primary w-full sm:w-auto disabled:opacity-60">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4" />} Send Enquiry
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Info */}
          <aside className="lg:col-span-5 space-y-4" data-testid="contact-info">
            <div className="bg-brand-navy-deep text-white rounded-2xl p-6 md:p-7">
              <div className="text-xs tracking-[0.25em] uppercase text-white/60 font-semibold">Reach Us Directly</div>
              <div className="space-y-5 mt-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center"><MapPin className="w-5 h-5 text-brand-crimson" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/60">Address</div>
                    <div className="mt-1 text-white/90 leading-relaxed">{HOSPITAL.address.line1}, {HOSPITAL.address.city}, {HOSPITAL.address.district}, {HOSPITAL.address.state} – {HOSPITAL.address.pincode}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center"><Phone className="w-5 h-5 text-brand-crimson" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/60">Phone · 24×7 Emergency</div>
                    <a href={`tel:${HOSPITAL.phoneRaw}`} className="mt-1 block text-white hover:underline font-semibold">{HOSPITAL.phone}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center"><Mail className="w-5 h-5 text-brand-crimson" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/60">Email</div>
                    <a href={`mailto:${HOSPITAL.email}`} className="mt-1 block text-white hover:underline">{HOSPITAL.email}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center"><Clock className="w-5 h-5 text-brand-crimson" /></div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-white/60">OPD Hours</div>
                    <div className="mt-1 text-white/90">Mon – Sat · 9:00 AM – 8:00 PM<br/>Sun · By Appointment</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-border">
              <iframe
                title="Godhra Heart Hospital Map"
                src={`https://maps.google.com/maps?q=${encodeURIComponent("Panjara Pole, Godhra, Panchmahal, Gujarat 389001")}&t=&z=14&ie=UTF8&iwloc=&output=embed`}
                className="w-full h-[280px] border-0"
                loading="lazy"
              />
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
